import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import {
  exportCsv,
  formatDuration,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';

const DAY_PRESETS = new Set(['today', 'yesterday']);

function formatDay(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
}

export function HoursReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('multi');

  // Período de até um dia: agrega tudo num resumo por máquina.
  // Período maior: pede ao Traccar uma linha por dia/máquina (daily=true).
  const isDaily = executed ? !DAY_PRESETS.has(executed.preset) : false;

  const query = useQuery({
    queryKey: ['report', 'hours', executed, isDaily],
    queryFn: () =>
      reportsApi.summary({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
        daily: isDaily,
      }),
    enabled: executed !== null,
  });

  const rows = useMemo(() => {
    const list = query.data ?? [];
    if (isDaily) {
      return [...list].sort((a, b) => {
        const da = a.startTime ?? '';
        const db = b.startTime ?? '';
        if (da !== db) return db.localeCompare(da); // mais recente primeiro
        return (a.deviceName ?? '').localeCompare(b.deviceName ?? '', 'pt-BR');
      });
    }
    return [...list].sort((a, b) => (b.engineHours ?? 0) - (a.engineHours ?? 0));
  }, [query.data, isDaily]);

  const total = rows.reduce((sum, r) => sum + (r.engineHours ?? 0), 0);

  const handleExport = () => {
    if (!executed) return;
    const header = isDaily
      ? ['Data', 'Máquina', 'Horas trabalhadas', 'Horas (ms)']
      : ['Máquina', 'Horas trabalhadas', 'Horas (ms)'];
    const body = rows.map((r) => {
      const machine = r.deviceName ?? devices.get(r.deviceId)?.name ?? '';
      const duration = r.engineHours != null ? formatDuration(r.engineHours) : '';
      const ms = r.engineHours ?? '';
      return isDaily ? [formatDay(r.startTime), machine, duration, ms] : [machine, duration, ms];
    });
    exportCsv(
      `horas_trabalhadas_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      header,
      body,
    );
  };

  return (
    <ReportShell
      title="Horas de Trabalho"
      description="Tempo total em operação por máquina no período. Em períodos maiores que um dia, mostra o detalhamento por dia."
    >
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && !query.isLoading && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Total de Horas Trabalhadas no período: <span className="font-semibold text-foreground">{formatDuration(total)}</span>
        </p>
      )}
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit={isDaily ? 'linhas (máquina · dia)' : 'máquinas'}
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {isDaily && <th className="px-4 py-2 font-semibold">Data</th>}
              <th className="px-4 py-2 font-semibold">Máquina</th>
              <th className="px-4 py-2 font-semibold">Horas trabalhadas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.deviceId}-${r.startTime ?? i}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                {isDaily && (
                  <td className="px-4 py-2 font-mono text-xs">{formatDay(r.startTime)}</td>
                )}
                <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                <td className="px-4 py-2 font-mono text-sm">
                  {r.engineHours != null ? formatDuration(r.engineHours) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTable>
    </ReportShell>
  );
}
